import { unstable_batchedUpdates } from 'react-dom';
import React, { createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import { reatomContext, setupBatch, withBatching } from '@reatom/npm-react';
import { breakPoints } from '@whitesharx/starfall-frontend-ui-kit';
import SayKitWeb, { UTMAttributesError } from '@whitesharx/starfall-frontend-saykit-web';
import { Metric, onCLS, onFID, onLCP, onTTFB, onFCP } from 'web-vitals';
import { SentryScript } from '~/src/components/SentryScript';
import { SafeGlobalThisScript } from '~/src/components/SafeGlobalThisScript';
import useResponsiveFlags from '~/src/services/responsive/useResponsiveFlags';
import { version } from '../../../package.json';
import { isClient, isMobileBrowser, isTabletBrowser } from '~/src/constants/env';
import { Analytics } from '~/src/services/analytics';
import AdsenseOnboarding from '~/src/services/adsense/onboarding';
import { firebaseInstance } from '~/src/services/firebase';
import { VIEW_EVENTS, UTM_EVENTS, PERFORMANCE_EVENTS } from '~/src/services/analytics/contants';
import { getOrGenerateUserPseudoId } from '~/src/utils/getOrGenerateUserPseudoId';
import useInstallPrompt from '~/src/hooks/useInstallPrompt';
import '../../../styles/globalStyles.css';
import '../../utils/all-settled-polyfill';
import useAppHeight from '~/src/hooks/useAppHeight';
import { connectLogger, createCtx } from '@reatom/framework';

setupBatch(unstable_batchedUpdates);
const onboardingCtx = withBatching(createCtx());
connectLogger(onboardingCtx);

export type OnboardingServicesContextType = {
  Analytics: Analytics;
  Adsense: AdsenseOnboarding;
  setMainResourcesLoaded: Dispatch<SetStateAction<boolean>>;
  isDesktop: boolean;
  isDeeplinkModalOpen: boolean;
  onDeeplinkClose: () => void;
};

export type OnboardingAppTypes = {
  skeleton?: boolean;
};

const formattedVersion = {
  development: '0.12.3',
  staging: '0.12.3',
  testing: '0.0.0',
  production: version.replace('-master', ''),
}[process.env.NEXT_RUNTIME];

const OnboardingServicesContext = createContext<OnboardingServicesContextType>({
  Analytics: {} as Analytics,
  setMainResourcesLoaded: () => undefined,
  isDesktop: false,
  Adsense: {} as AdsenseOnboarding,
  isDeeplinkModalOpen: false,
  onDeeplinkClose: () => undefined,
});

const DEEPLINK_SHOWN = 'deeplink_shown';

const OnboardingServicesProvider: FC = ({ children }) => {
  const { isDesktop } = useResponsiveFlags(breakPoints);
  const analytics = useMemo(() => new Analytics(firebaseInstance), []);
  const adsense = useMemo(() => new AdsenseOnboarding(analytics), []);
  const [mainResourcesLoaded, setMainResourcesLoaded] = useState(false);

  const canShowDeeplink = (isMobileBrowser || isTabletBrowser) && !sessionStorage.getItem(DEEPLINK_SHOWN);
  const [isDeeplinkModalOpen, setIsDeeplinkModalOpen] = useState(false);

  const onDeeplinkClose = () => {
    setIsDeeplinkModalOpen(false);
    sessionStorage.setItem(DEEPLINK_SHOWN, 'true');
  };

  useInstallPrompt();
  useAppHeight();

  useEffect(() => {
    const userPseudoId = getOrGenerateUserPseudoId();

    SayKitWeb.getRemoteConfig(
      {
        appKey: process.env.SAYKIT_APP_KEY,
        version: formattedVersion,
        deviceId: userPseudoId,
        storage: window.storage,
      },
      window.Sentry?.captureException
    )
      .then(() => {
        SayKitWeb.initialize(0);

        setIsDeeplinkModalOpen(Boolean(SayKitWeb.featureFlags.deeplink) && canShowDeeplink);
      })
      .then(() => analytics.setUserId(userPseudoId))
      .then(() => {
        SayKitWeb.trackUTMParameters()
          .then((attributes) => {
            console.log('UTM Attributes sent successfuly');

            analytics.sendEvent(UTM_EVENTS.sent, { attributes });
          })
          .catch((error: UTMAttributesError) => {
            if (error.code === 'NO_ATTRS') {
              return;
            }

            console.error('Failed to fetch UTM Attributions', error);

            analytics.sendEvent(UTM_EVENTS.failed, { attributes: error.attributes! });
          });
      });

    const handleLoad = () => {
      analytics.sendEvent(VIEW_EVENTS.appLoaded, { time: (performance.now() / 1000).toFixed(2) });
    };

    window.addEventListener('load', handleLoad);

    const handleMetric = (name: string) => (metric: Metric) => {
      analytics.sendEvent(name, {
        time: String(metric.value),
        rating: metric.rating,
        navigationType: metric.navigationType,
        pathname: location.pathname,
      });
    };

    onCLS(handleMetric(PERFORMANCE_EVENTS.cls));
    onFID(handleMetric(PERFORMANCE_EVENTS.fid));
    onLCP(handleMetric(PERFORMANCE_EVENTS.lcp));
    onFCP(handleMetric(PERFORMANCE_EVENTS.fcp));
    onTTFB(handleMetric(PERFORMANCE_EVENTS.ttfb));

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  useEffect(() => {
    const callback = () => {
      if (mainResourcesLoaded) {
        adsense.load();
        analytics.load();
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 1);
    }
  }, [mainResourcesLoaded]);

  return (
    <OnboardingServicesContext.Provider
      value={{
        Analytics: analytics,
        Adsense: adsense,
        setMainResourcesLoaded,
        isDesktop,
        isDeeplinkModalOpen,
        onDeeplinkClose,
      }}
    >
      {children}
    </OnboardingServicesContext.Provider>
  );
};

export const useOnboardingServices = () => useContext(OnboardingServicesContext);

const OnboardingCustomApp = ({ Component, pageProps, router }: AppProps) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta property="og:url" content={origin} />
        <meta property="og:image" content={`${origin}/assets/images/chess.png`} />
        {router.pathname === '/onboarding/game' && <link href={process.env.UI_KIT_STYLES_PATH} rel="stylesheet" />}
        {router.pathname === '/onboarding' && <link href={process.env.UI_KIT_STYLES_PATH} rel="prefetch" as="style" />}
        {router.pathname === '/onboarding' && <link href="/assets/images/guest-board.avif" rel="preload" as="image" />}

        {SentryScript()}
        {SafeGlobalThisScript()}
      </Head>

      <OnboardingServicesProvider>
        <Component {...pageProps} skeleton={!isClient} />
      </OnboardingServicesProvider>
    </>
  );
};

export default (props: AppProps) => (
  <reatomContext.Provider value={onboardingCtx}>
    <OnboardingCustomApp {...props} />
  </reatomContext.Provider>
);
